import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Receipt, Users as UsersIcon } from "lucide-react";

const CATEGORIES = [
  "Groceries", "Laundry/Dry Cleaning", "Maid Salary", "Watchman/Security Salary",
  "Driver Salary", "Cook Salary", "Utilities - Electricity", "Utilities - Water",
  "Utilities - Gas", "Maintenance/Repairs", "Other Household Services",
  "Other Categories (Not Listed Above)"
];

const fmt = (n) => n != null ? parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-';
const newItemId = () => `li_${Math.random().toString(36).slice(2, 10)}`;

export default function TransactionModal({
  open, onOpenChange, mode, form, setForm, lineItems, setLineItems,
  members, splittingEnabled, saving, onSave,
}) {
  const [tab, setTab] = useState("details");

  useEffect(() => { if (open) setTab("details"); }, [open]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addLineItem = () => {
    setLineItems(items => [...items, {
      id: newItemId(), description: "", amount: 0, category: form.category || "",
      split_override: null,
    }]);
  };
  const updateItem = (id, patch) => {
    setLineItems(items => items.map(it => it.id === id ? { ...it, ...patch } : it));
  };
  const removeItem = (id) => setLineItems(items => items.filter(it => it.id !== id));

  const toggleItemMember = (item, memberId) => {
    const current = item.split_override?.member_ids || [];
    const next = current.includes(memberId)
      ? current.filter(m => m !== memberId)
      : [...current, memberId];
    updateItem(item.id, {
      split_override: next.length > 0 ? { member_ids: next, split_type: "equal" } : null
    });
  };

  const itemSum = lineItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const hasLineItems = lineItems.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="transaction-modal">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-stone-900">
            {mode === 'add' ? 'Add Transaction' : 'Edit Transaction'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`grid ${splittingEnabled ? 'grid-cols-2' : 'grid-cols-1'} bg-stone-100 rounded-xl p-1`}>
            <TabsTrigger value="details" className="rounded-lg" data-testid="tx-tab-details"><Receipt className="w-3.5 h-3.5 mr-1.5" />Details</TabsTrigger>
            {splittingEnabled && (
              <TabsTrigger value="split" className="rounded-lg" data-testid="tx-tab-split"><UsersIcon className="w-3.5 h-3.5 mr-1.5" />Split</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="details" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Date</Label>
                <Input type="date" value={form.date} onChange={e => setField('date', e.target.value)} className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-date" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Vendor</Label>
                <Input value={form.vendor_name} onChange={e => setField('vendor_name', e.target.value)} placeholder="e.g. Licious" className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-vendor" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Category</Label>
              <Select value={form.category} onValueChange={v => setField('category', v)}>
                <SelectTrigger className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Description</Label>
              <Input value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Item details" className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Amount (₹)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setField('amount', e.target.value)} placeholder="0.00" className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-amount" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Deduction (₹)</Label>
                <Input type="number" step="0.01" value={form.deduction} onChange={e => setField('deduction', e.target.value)} placeholder="0.00" className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-deduction" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Notes / Info</Label>
              <Input value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="e.g. Wallet credit used" className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-notes" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Invoice Link</Label>
              <Input value={form.receipt_link} onChange={e => setField('receipt_link', e.target.value)} placeholder="https://..." className="mt-1 border-2 border-stone-200 rounded-xl" data-testid="modal-link" />
            </div>
            <div className="bg-stone-50 rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-stone-500 uppercase">Final Amount</span>
              <span className="text-lg font-heading font-extrabold text-stone-900">₹{fmt((parseFloat(form.amount) || 0) - (parseFloat(form.deduction) || 0))}</span>
            </div>
          </TabsContent>

          {splittingEnabled && (
            <TabsContent value="split" className="space-y-3 pt-4">
              {members.length === 0 ? (
                <p className="text-sm text-stone-400 italic">Add members to this configuration first.</p>
              ) : (
                <>
                  <div className="text-xs text-stone-500">
                    Each line item is split equally across members by default. Tap a member chip to charge only selected members for that line.
                  </div>

                  {!hasLineItems && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                      No line items yet. Either the invoice had none extracted, or this is a manual entry. Add line items below to enable per-item splitting — otherwise the full amount will be split equally.
                    </div>
                  )}

                  <div className="space-y-2">
                    {lineItems.map(item => {
                      const overrideIds = item.split_override?.member_ids || [];
                      const usingOverride = overrideIds.length > 0;
                      const effective = item.effective_split;
                      // Show effective members (override | rule | default)
                      const effectiveIds = usingOverride
                        ? overrideIds
                        : (effective?.member_ids || members.map(m => m.id));
                      const effectiveNames = effectiveIds
                        .map(mid => members.find(m => m.id === mid)?.name)
                        .filter(Boolean);
                      const effectiveSource = usingOverride ? "override" : (effective?.source || "default");
                      return (
                        <div key={item.id} className={`p-3 rounded-xl border bg-white space-y-2 transition ${usingOverride ? 'border-sage-300 bg-sage-50/30' : 'border-stone-200'}`} data-testid={`line-item-${item.id}`}>
                          <div className="flex items-center gap-2">
                            <Input value={item.description}
                              onChange={e => updateItem(item.id, { description: e.target.value })}
                              placeholder="Item description"
                              className="flex-1 border border-stone-200 rounded-lg text-sm h-9" />
                            <Input type="number" step="0.01" value={item.amount}
                              onChange={e => updateItem(item.id, { amount: parseFloat(e.target.value) || 0 })}
                              placeholder="₹"
                              className="w-24 border border-stone-200 rounded-lg text-sm h-9" />
                            <button onClick={() => removeItem(item.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500" data-testid={`remove-item-${item.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-[11px] text-stone-600 flex items-center gap-1.5 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${effectiveSource === 'override' ? 'bg-[#4F6F52] text-white' : effectiveSource === 'rule' ? 'bg-blue-100 text-blue-800' : 'bg-stone-100 text-stone-600'}`}>
                                {effectiveSource === 'override' ? 'Charged to' : effectiveSource === 'rule' ? 'Via rule' : 'Split between'}
                              </span>
                              <span className="font-semibold text-stone-700">{effectiveNames.join(", ") || '—'}</span>
                              {effectiveSource === 'rule' && effective?.matched_rule && (
                                <span className="text-[10px] text-stone-400">({effective.matched_rule.scope}: "{effective.matched_rule.match_value}")</span>
                              )}
                            </div>
                            {usingOverride && (
                              <button type="button" onClick={() => updateItem(item.id, { split_override: null })}
                                className="text-[10px] font-semibold text-stone-400 hover:text-stone-700 underline underline-offset-2"
                                data-testid={`clear-override-${item.id}`}>
                                Clear
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {members.map(m => {
                              const isEffective = effectiveIds.includes(m.id);
                              const isOverrideOn = overrideIds.includes(m.id);
                              // Style: override selected = solid + shadow; rule/default effective = tinted; unselected = plain
                              const style = isOverrideOn ? { background: m.color } :
                                            isEffective ? { background: `${m.color}22`, color: m.color, borderColor: `${m.color}55` } : {};
                              const cls = isOverrideOn
                                ? 'text-white border-transparent shadow-sm'
                                : isEffective
                                  ? 'border font-semibold'
                                  : 'bg-white text-stone-400 border-stone-200 hover:border-stone-400';
                              return (
                                <button key={m.id} type="button"
                                  onClick={() => toggleItemMember(item, m.id)}
                                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition border ${cls}`}
                                  style={style}
                                  data-testid={`item-${item.id}-member-${m.id}`}>
                                  {m.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button onClick={addLineItem} variant="outline" className="w-full rounded-xl border-dashed" data-testid="add-line-item-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Line Item
                  </Button>

                  {hasLineItems && (
                    <div className="text-xs text-stone-500 flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2">
                      <span>Line items total</span>
                      <span className="font-semibold text-stone-700 tabular-nums">₹{fmt(itemSum)}</span>
                    </div>
                  )}
                  {hasLineItems && Math.abs(itemSum - (parseFloat(form.amount) || 0)) >= 0.5 && (
                    <div className="text-[11px] text-stone-500 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2">
                      ℹ A gap of ₹{fmt(Math.abs(itemSum - (parseFloat(form.amount) || 0)))} between line items and the invoice total will be added as “Fees &amp; charges” and split equally across all members.
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#4F6F52] hover:bg-[#3f5942] rounded-xl" data-testid="modal-save">
            {saving ? 'Saving...' : mode === 'add' ? 'Add Transaction' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
